/*
 * COMING BACK TO THE APP — the wake-up liveness check (roadmap stage 2 item 4,
 * owed since decision 324).
 *
 * WHAT WAS BROKEN. The Active tab is conditional on `active_event_count`
 * (decision 324), and that count was fetched on a cold load and on a return to
 * "/" and nowhere else. So a coach who opened the app on the drive to a venue
 * and pocketed the phone took it out again to no Active tab, on the one morning
 * the tab is the entire point of the product. A reload fixed it — which is
 * exactly the instruction this app exists to remove.
 *
 * TWO THINGS RE-ASK ON WAKE, and pairing them is what the roadmap's sequencing
 * note asked for: it is one question ("how old is this, and when do we
 * re-ask") seen from the chrome and from the screen.
 *
 *   1. THE TAB COUNT, unconditionally, in main.tsx. It is one small request and
 *      the thing it decides is whether a whole screen is reachable.
 *   2. THE VIEW THE USER IS ON, but only if what it is showing has aged past
 *      its regime's step — `shouldRefreshOnWake` below.
 *
 * WHY THE VIEW IS CONDITIONAL AND THE COUNT IS NOT: switching to another tab
 * and straight back is the common case, and refetching a whole Upcoming payload
 * every time someone glances at their calendar spends requests to redraw an
 * identical screen.
 */

import { useCallback, useEffect } from "react";

import { api } from "./api";
import type { Regime } from "./staleness";

/**
 * How stale a view's data must be before a return to the foreground refetches
 * it, in minutes, per staleness regime.
 *
 * KEYED ON WHEN WE LAST ASKED, not on the payload's `observed_at`, and that is
 * the decision rather than a convenience. Re-asking cannot make `observed_at`
 * any newer if ingestion has not run since — so an observed_at trigger would
 * fire on every single wake during a quiet stretch, spend a request, and redraw
 * the same numbers. Our own fetch age answers the question that actually has a
 * remedy: "might the server know something we have not asked for?"
 *
 * DAY-OF IS FIVE MINUTES, and it is NO LONGER `DAY_OF_AGING_MINUTES` — the two
 * were the same number and decision 462 separated them, which is worth stating
 * because the tie looked like a principle. Twenty minutes is now when the app
 * starts SAYING a reading is old; five is how often it is willing to go and
 * ask. Those are different questions: asking is cheap, the poller writes every
 * ninety seconds, and a wake that refetches is how a phone taken out of a
 * pocket gets the current strip. Waiting twenty minutes to re-ask would make
 * the app quieter about staleness and staler in fact.
 *
 * PLANNING IS SIXTY MINUTES, because in that regime hours are normal and saying
 * otherwise is a lie the design forbids (§10: never faded, never ambered — "a
 * registration list read this morning is not stale"). An hour is long enough
 * that a wake costs nothing on a day of tab-switching, and short enough that
 * the app someone left open overnight is not showing yesterday's deadlines.
 */
export const REFRESH_AFTER_MINUTES: Record<Regime, number> = {
  "day-of": 5,
  planning: 60,
};

/**
 * Whether a view holding data fetched at `fetchedAt` should refetch on waking.
 *
 * A null `fetchedAt` means nothing has loaded yet, which is NOT a reason to
 * refetch: the first fetch is either still in flight or it failed, and firing a
 * second one on a wake would double up on the in-flight case and hammer the
 * failed one every time the tab is touched.
 *
 * Pure, and separated from the listener below, so the rule is testable without
 * a DOM and a future native client reimplements one documented function rather
 * than inferring the policy from a component.
 */
export function shouldRefreshOnWake(
  fetchedAt: number | null,
  regime: Regime,
  now: number = Date.now(),
): boolean {
  if (fetchedAt === null) return false;

  const elapsedMinutes = (now - fetchedAt) / 60_000;
  return elapsedMinutes >= REFRESH_AFTER_MINUTES[regime];
}

/**
 * Runs `onWake` whenever the tab or installed app becomes active again.
 *
 * `visibilitychange` FILTERED ON `visible`, because the event fires in both
 * directions and the hiding half is not a wake — reacting to it would fire a
 * request as the user leaves, which is the opposite of the point.
 *
 * This is the event a PWA gets on iOS and Android when the installed app is
 * foregrounded, which is why item 4 named it alongside "the equivalent
 * lifecycle event on a native client": on the web they are the same event, so
 * an installed ThirdCall needs no second listener. A native client re-points
 * this at its own lifecycle hook and keeps everything above it.
 *
 * NOT `focus`. Focus fires when a window is clicked back into from another
 * application while the tab was never hidden, so it would refetch on every
 * alt-tab through the browser; and it does NOT fire for the case this exists
 * for, a phone coming out of a pocket.
 *
 * `onWake` is read through a ref-free dependency on purpose: callers pass a
 * fresh closure each render (it reads their current state), and re-subscribing
 * is cheap next to getting a stale one.
 */
export function useWakeUp(onWake: () => void): void {
  useEffect(() => {
    function handler() {
      if (document.visibilityState === "visible") onWake();
    }

    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [onWake]);
}

/** The facts the app SHELL draws itself from, all served by /api/me. */
export interface ShellCounts {
  /** Live events; what Active's "live on another list" sentence reads (503). */
  activeEventCount: number;
  /**
   * The Active tab's own two facts (decision 546). The tab used to ride on the
   * count above, which stays true for a day after the last fencer has finished;
   * `activeTabVisible` ages these with the window that retires a fencer card.
   */
  activeFencerCount: number;
  lastFencerDoneAt?: string;
  /** Unread alerts; zero hides the badge but never the entry (decision 401). */
  unreadAlertCount: number;
}

/**
 * Re-asks the shell's counts when the app comes back to the foreground.
 *
 * This is the CHROME's half of the wake-up check — the half that decides
 * whether the Active tab exists at all (decision 324) — and it is
 * unconditional, unlike a view's refresh: it is one small request, and what it
 * decides is whether a whole screen is reachable.
 *
 * `/api/me` rather than `/api/active`, because the counts are the entire answer
 * the chrome needs and /api/me is the smaller payload. That is the same
 * reasoning decision 324 used to put `active_event_count` there rather than
 * making the shell fetch a payload it would discard.
 *
 * THE UNREAD ALERT COUNT RIDES ALONG FOR FREE, which is the whole argument
 * stage 3 made for putting it on /api/me (§4.1, decision 401): this listener
 * already exists, already fires when a phone comes out of a pocket, and already
 * reads the one endpoint that carries it. A notifications badge that needed a
 * timer would be a second refresh policy to keep in step with this one.
 *
 * NEITHER COUNT IS ZEROED ON FAILURE. Decision 324's own rule, and the reason
 * the failure path here reports NOTHING rather than reporting zero: a request
 * that failed is not evidence that nothing is live. Zeroing would hide the
 * Active tab on one dropped packet — at a venue, on the morning the tab is the
 * whole point of the product — and would clear an unread badge nobody had read.
 * Waking up in a tunnel leaves both exactly as they were, which is the honest
 * answer: we do not know, and what we knew last is better than a fresh lie.
 */
export function useLivenessOnWake(onCounts: (counts: ShellCounts) => void): void {
  useWakeUp(
    useCallback(() => {
      api
        .me()
        .then(({ activeEventCount, activeFencerCount, lastFencerDoneAt, unreadAlertCount }) =>
          // `lastFencerDoneAt` travels even when it is undefined, and that
          // matters: a wake-up that finds nobody finished must CLEAR the stamp
          // rather than leave the previous one standing, or the tab would
          // outlive the fact that put it there.
          onCounts({ activeEventCount, activeFencerCount, lastFencerDoneAt, unreadAlertCount }),
        )
        .catch(() => {
          // Deliberately nothing. See above: a failure is not a zero.
        });
    }, [onCounts]),
  );
}
